import React, { useState } from 'react';
import { Box, Button, ButtonGroup, CircularProgress, Snackbar, Alert } from '@mui/material';
import LockIcon from '@mui/icons-material/Lock';
import PublicIcon from '@mui/icons-material/Public';
import { useFieldVisibility } from '../hooks/useFieldVisibility';
import { logger } from '../utils/logger';

type ObjectStatus = 'all-public' | 'all-private' | 'mixed';

interface ObjectPrivacyControlsProps {
  /** Image visibility status from useUploadImageVisibility */
  imageStatus?: 'all-public' | 'all-private' | 'mixed';
  /** Callback to set all images public */
  onSetAllImagesPublic?: () => Promise<void>;
  /** Callback to set all images private */
  onSetAllImagesPrivate?: () => Promise<void>;
}

/**
 * Object-level privacy controls — single toggle to make entire object public or private.
 * Combines field visibility (all sections) with image visibility status.
 */
export const ObjectPrivacyControls: React.FC<ObjectPrivacyControlsProps> = ({
  imageStatus,
  onSetAllImagesPublic,
  onSetAllImagesPrivate,
}) => {
  const {
    isObjectPublic,
    isObjectPrivate,
    makeObjectPublic,
    makeObjectPrivate,
    isLoading,
  } = useFieldVisibility();

  const [actionLoading, setActionLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const getStatus = (): ObjectStatus => {
    if (isLoading) return 'all-private';

    const fieldsPublic = isObjectPublic();
    const fieldsPrivate = isObjectPrivate();

    const imagesPublic = !imageStatus || imageStatus === 'all-public';
    const imagesPrivate = !imageStatus || imageStatus === 'all-private';

    if (fieldsPublic && imagesPublic) return 'all-public';
    if (fieldsPrivate && imagesPrivate) return 'all-private';
    return 'mixed';
  };

  const status = getStatus();

  const extractErrorMessage = (error: unknown, fallback: string): string =>
    error instanceof Error ? error.message : fallback;

  const handleMakePublic = async () => {
    setActionLoading(true);
    setErrorMessage(null);
    try {
      await makeObjectPublic();
      await onSetAllImagesPublic?.();
    } catch (error) {
      const message = extractErrorMessage(error, 'Failed to make object public');
      setErrorMessage(message);
      logger.debug('Failed to make object public', error);
    } finally {
      setActionLoading(false);
    }
  };

  const handleMakePrivate = async () => {
    setActionLoading(true);
    setErrorMessage(null);
    try {
      await makeObjectPrivate();
      await onSetAllImagesPrivate?.();
    } catch (error) {
      const message = extractErrorMessage(error, 'Failed to make object private');
      setErrorMessage(message);
      logger.debug('Failed to make object private', error);
    } finally {
      setActionLoading(false);
    }
  };

  const buttonsDisabled = isLoading || actionLoading;

  return (
    <>
      <Box display="flex" alignItems="center" gap={1} flexWrap="wrap">
        <ButtonGroup size="small" variant="outlined">
          <Button
            onClick={handleMakePublic}
            disabled={buttonsDisabled || status === 'all-public'}
            startIcon={actionLoading ? <CircularProgress size={14} /> : <PublicIcon sx={{ fontSize: 14 }} />}
            sx={{ textTransform: 'none' }}
          >
            Make all public
          </Button>
          <Button
            onClick={handleMakePrivate}
            disabled={buttonsDisabled || status === 'all-private'}
            startIcon={actionLoading ? <CircularProgress size={14} /> : <LockIcon sx={{ fontSize: 14 }} />}
            sx={{ textTransform: 'none' }}
          >
            Make all private
          </Button>
        </ButtonGroup>
      </Box>
      <Snackbar
        open={!!errorMessage}
        autoHideDuration={5000}
        onClose={() => setErrorMessage(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert severity="error" onClose={() => setErrorMessage(null)}>
          {errorMessage}
        </Alert>
      </Snackbar>
    </>
  );
};
